// Shared phone validation & normalization — single source of truth for every
// flow that touches a phone number (AddLead, SignUp, Directory CSV import).
//
// Mirrors leads-service's Leads\Domain\Services\PhoneNormalizer (both use
// Google's libphonenumber metadata, so verdicts agree):
// - Validation always happens on the dialable E.164 form (+<cc><national>).
// - The stored handle is E.164 digits without '+', with one legacy quirk:
//   Mexican handles carry a '1' between country code and the 10-digit national
//   number (WhatsApp JID convention, e.g. 5215512345678). That '1' is NOT
//   dialable and libphonenumber rejects it, so it is inserted or tolerated
//   only AFTER the +52 form validated.
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

const MX_COUNTRY_CODE = '52';
const MX_WHATSAPP_PREFIX = '521';
const MX_E164_DIGITS = 12; // 52 + 10-digit national number
const MX_HANDLE_DIGITS = 13; // 521 + 10-digit national number

export type tPhoneValidationReason =
  | 'phone_required'
  | 'area_code_required'
  | 'invalid_area_code'
  | 'invalid_phone';

export type tPhoneNormalizationResult =
  | { valid: true; handle: string }
  | { valid: false; reason: tPhoneValidationReason };

export function sanitizePhoneDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/**
 * Validate a phone string against a country (iso2). Same semantics the AddLead
 * and SignUp forms used inline before extraction.
 */
export function isPhoneValid(phone: string, country: string): boolean {
  try {
    const parsed = phoneUtil.parseAndKeepRawInput(phone, country.toUpperCase());
    return phoneUtil.isValidNumber(parsed);
  } catch {
    return false;
  }
}

/**
 * Validate + normalize a CSV (lada, teléfono) pair into a lead handle.
 */
export function normalizePhonePair(
  areaCode: string | null | undefined,
  phone: string | null | undefined,
): tPhoneNormalizationResult {
  const areaDigits = sanitizePhoneDigits(areaCode);
  const phoneDigits = sanitizePhoneDigits(phone);

  if (phoneDigits === '') return { valid: false, reason: 'phone_required' };
  if (areaDigits === '') return { valid: false, reason: 'area_code_required' };

  return normalizeDigits(areaDigits + phoneDigits);
}

/**
 * Validate + normalize a full handle (country code included, no '+').
 */
export function normalizeHandle(
  handle: string | null | undefined,
): tPhoneNormalizationResult {
  const digits = sanitizePhoneDigits(handle);

  if (digits === '') return { valid: false, reason: 'phone_required' };

  return normalizeDigits(digits);
}

/**
 * Build the lead handle the way createNewLead needs it: from a dial code and
 * the national number typed by the user. Owns the MX 521 rule that used to
 * live inline in Pipe.hook.
 */
export function buildLeadHandle(
  countryCode: string,
  nationalNumber: string,
): tPhoneNormalizationResult {
  return normalizePhonePair(countryCode, nationalNumber);
}

/** El país de un handle, para decir de dónde sale la tarifa (RET-2232). */
export type tHandleCountry = {
  /** ISO2 en mayúsculas, p. ej. `MX`. */
  iso2: string;
  /** Código de marcación SIN `+`, p. ej. `52`. */
  callingCode: string;
};

/**
 * El país al que pertenece un handle, o `null` si no se puede afirmar.
 *
 * Existe para el tooltip del chip «Con costo» de la lista de plantillas
 * (RET-2232): Meta cobra por tipo de plantilla × país del destinatario, y el
 * estimador NO devuelve el país — sólo el monto. La región sale del número.
 *
 * Devuelve `null` en vez de adivinar. El tooltip entonces dice sólo su primera
 * oración, que es cierta siempre; inventar un país en una explicación sobre
 * dinero es el mismo error que pintar un `$0.00` cuando no se pudo cotizar.
 *
 * Pasa por `normalizeHandle` primero porque de ahí sale la VALIDEZ: sin ese
 * filtro, cualquier cadena con un prefijo que parezca código de país
 * resolvería un país. La región en sí NO necesita el quirk del `521`
 * mexicano que este archivo documenta arriba —`getRegionCodeForNumber` la
 * deriva del código de país, y `+5215512345678` ya dice `52`—; una versión
 * anterior de esta función traía una rama para eso y era código muerto,
 * verificado quitándola con la suite en verde.
 */
export function resolveHandleCountry(
  handle: string | null | undefined,
): tHandleCountry | null {
  const normalized = normalizeHandle(handle);

  if (!normalized.valid) return null;

  try {
    const parsed = phoneUtil.parseAndKeepRawInput(`+${normalized.handle}`, 'ZZ');
    // `getRegionCodeForNumber` está tipada como un union de ISO2 reales, pero
    // en runtime devuelve `undefined` o `'ZZ'` para un número sin región. Se
    // compara como string para poder descartar los dos sin mentirle a TS.
    const iso2 = String(phoneUtil.getRegionCodeForNumber(parsed) ?? '');
    const callingCode = parsed.getCountryCode();

    if (!iso2 || iso2 === 'ZZ' || !callingCode) return null;

    return { iso2: iso2.toUpperCase(), callingCode: String(callingCode) };
  } catch {
    return null;
  }
}

function normalizeDigits(digits: string): tPhoneNormalizationResult {
  let parseFailure: tPhoneValidationReason | null = null;

  try {
    const parsed = phoneUtil.parseAndKeepRawInput(`+${digits}`, 'ZZ');
    if (phoneUtil.isValidNumber(parsed)) {
      const e164Digits = phoneUtil
        .format(parsed, PhoneNumberFormat.E164)
        .slice(1);
      return { valid: true, handle: toHandle(e164Digits) };
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('Invalid country calling code')
    ) {
      parseFailure = 'invalid_area_code';
    }
  }

  // Legacy MX handle (521 + 10 digits): accept when its dialable +52 form is valid.
  if (
    digits.startsWith(MX_WHATSAPP_PREFIX) &&
    digits.length === MX_HANDLE_DIGITS
  ) {
    try {
      const mx = phoneUtil.parseAndKeepRawInput(
        `+${MX_COUNTRY_CODE}${digits.slice(3)}`,
        'ZZ',
      );
      if (phoneUtil.isValidNumber(mx)) {
        return { valid: true, handle: digits };
      }
    } catch {
      // fall through
    }
  }

  return { valid: false, reason: parseFailure ?? 'invalid_phone' };
}

function toHandle(e164Digits: string): string {
  if (
    e164Digits.startsWith(MX_COUNTRY_CODE) &&
    e164Digits.length === MX_E164_DIGITS
  ) {
    return `${MX_WHATSAPP_PREFIX}${e164Digits.slice(2)}`;
  }
  return e164Digits;
}

/**
 * Render E.164 digits (no leading '+') in libphonenumber's INTERNATIONAL
 * grouping, or null when they are not a valid dialable number.
 *
 * The read-path counterpart to normalizeHandle. It lives here so this module
 * stays the only place that imports google-libphonenumber: the library is a
 * 563 kB Closure-compiled metadata blob, and one import site keeps its cost
 * attributable to one module instead of appearing twice in the graph.
 *
 * Note this does NOT keep the blob out of the eager bundle. Rollup places it
 * wherever it is statically reachable from, and MessageSearch.identity is
 * reached from AdvanceSearch, which main imports eagerly — so the 563 kB
 * `phone.functions` chunk now sits inside `main`. Moving it back out needs
 * AdvanceSearch (or the formatting itself) to become lazy, not a second
 * import site removed.
 */
export function formatE164Digits(digits: string): string | null {
  try {
    const parsed = phoneUtil.parseAndKeepRawInput(`+${digits}`, 'ZZ');

    if (!phoneUtil.isValidNumber(parsed)) return null;

    return phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL);
  } catch {
    return null;
  }
}
